import {
  AttentionBlock,
  Button,
  EmptyState,
  PageHeader,
  PageTitle,
  RowActions,
  SearchInput,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  titleCase,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { useSession } from '@/lib/session'
import { useDebounced } from '@/lib/use-debounced'
import { routes } from '@/routes'
import { useTeam } from './api'
import { OUTSIDE_EMAIL_EFFECT, REMOVE_ANCHOR, lastSignInText, teamStatus } from './labels'

/**
 * A equipe da Westhetics (docs/areas/administracao.md, "Equipe da Westhetics"; RN-AUT-28 a 30).
 * Arquétipo: lista de trabalho, de root, no grupo "Westhetics" da sidebar; não depende de clínica
 * ativa. Cada linha é uma pessoa com o acesso de root: vê todas as clínicas e entra em qualquer uma.
 */

export function TeamPage() {
  const session = useSession()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounced(search.trim())
  const query = useTeam({ search: debouncedSearch || undefined, page })
  const members = query.data?.data ?? []
  const total = query.data?.meta.total ?? 0
  const showSearch = total > 20 || search !== ''
  // RN-AUT-31: enquanto ninguém da equipe tem e-mail da empresa, quem está fora continua e é marcado.
  const outside = members.filter((member) => !member.hasAllowedEmail)
  const selfOutside = outside.some((member) => member.id === session.profile?.userId)

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Westhetics' }, { label: 'Equipe' }]}
        actions={
          <Button size="sm" asChild>
            <Link to={routes.admin.newTeamMember}>+ Adicionar à equipe</Link>
          </Button>
        }
      />
      <Page wide>
        <PageTitle
          title="Equipe da Westhetics"
          subtitle="Quem administra a Westhetics: vê todas as clínicas, entra em qualquer uma com os poderes de gestora e cuida do catálogo, dos contratos e das redes."
        />

        {query.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar a equipe"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            {showSearch ? (
              <div className="mt-5">
                <SearchInput
                  className="tablet:max-w-[300px]"
                  value={search}
                  onChange={(value) => {
                    setSearch(value)
                    setPage(1)
                  }}
                  placeholder="Buscar por nome ou e-mail"
                  aria-label="Buscar na equipe"
                />
              </div>
            ) : null}

            {outside.length > 0 ? (
              <AttentionBlock
                className="mt-5"
                title={
                  outside.length === 1
                    ? `${titleCase(outside[0]!.name)} tem e-mail de fora da empresa e vai sair da equipe`
                    : `${outside.length} pessoas têm e-mail de fora da empresa e vão sair da equipe`
                }
              >
                {OUTSIDE_EMAIL_EFFECT}
                {selfOutside
                  ? ' A sua conta está entre elas: para continuar na equipe, adicione o seu e-mail da empresa e entre com ele.'
                  : null}
              </AttentionBlock>
            ) : null}

            <div className="mt-4">
              {query.isPending ? (
                <TableSkeleton rows={3} columns={5} />
              ) : members.length === 0 ? (
                debouncedSearch ? (
                  <EmptyState
                    title={`Ninguém da equipe para "${debouncedSearch}"`}
                    description="A busca cobre o nome e o e-mail de quem é da equipe da Westhetics."
                    action={
                      <Button variant="outline" onClick={() => setSearch('')}>
                        Limpar busca
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState
                    title="Ninguém na equipe ainda"
                    description="A equipe é quem administra a Westhetics. Cada pessoa recebe o convite por e-mail e cria a própria senha."
                    action={
                      <Button asChild>
                        <Link to={routes.admin.newTeamMember}>Adicionar a primeira pessoa</Link>
                      </Button>
                    }
                  />
                )
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHead>Nome</TableHead>
                      <TableHead>E-mail</TableHead>
                      <TableHead>Último acesso</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead aria-label="Ações" />
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {members.map((member) => {
                      const status = teamStatus(member)
                      // Ninguém tira a própria conta da equipe (RN-AUT-15): a ação some da linha.
                      const isSelf = member.id === session.profile?.userId
                      return (
                        <TableRow
                          key={member.id}
                          onClick={() => navigate(routes.admin.teamMember(member.id))}
                        >
                          <TableCell primary>
                            {titleCase(member.name)}
                            {isSelf ? <span className="text-secondary"> · você</span> : null}
                          </TableCell>
                          <TableCell label="E-mail" muted className="break-all">
                            {member.email}
                          </TableCell>
                          <TableCell label="Último acesso" muted>
                            {lastSignInText(member.lastSignInAt)}
                          </TableCell>
                          <TableCell label="Status">
                            <StatusTag level={status.level}>{status.label}</StatusTag>
                          </TableCell>
                          <TableCell>
                            <RowActions>
                              <Link to={routes.admin.teamMember(member.id)}>abrir</Link>
                              {isSelf ? null : (
                                <Link
                                  to={{
                                    pathname: routes.admin.teamMember(member.id),
                                    hash: REMOVE_ANCHOR,
                                  }}
                                >
                                  tirar da equipe
                                </Link>
                              )}
                            </RowActions>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
              {query.data && members.length > 0 ? (
                <ListFooter
                  meta={query.data.meta}
                  noun={['pessoa', 'pessoas']}
                  note="em ordem alfabética"
                  onPage={setPage}
                />
              ) : null}
            </div>
          </>
        )}
      </Page>
    </>
  )
}
